import React from 'react';
import { Crown, Pencil, Trash2 } from 'lucide-react';
import { Button, Chip } from 'common/components';
import { Table, TableColumn } from 'common/components/Table';
import type { Member } from 'types/accessManager.type';
import { PRODUCT_LABELS, hasAnyRole, namesProducts, productsWithRoles, roleLabel } from '../../products';
import { UsersSectionProps } from './UsersSection.types';
import styles from './UsersSection.module.scss';

// The people the caller may see, with their roles in the caller's products.
// What is shown and offered comes from the backend: a product the caller
// does not manage is neither listed nor editable.
const UsersSection = ({
    members,
    manageable,
    isLoading,
    companyName,
    onAddUser,
    onInviteUser,
    onEditUser,
    onDeleteUser,
    onMakeOwner,
    emptyText = 'No users found',
    isStaff = false,
}: UsersSectionProps) => {
    const canManage = hasAnyRole(manageable);
    const named = namesProducts(manageable);

    const columns: TableColumn<Member>[] = [
        {
            key: 'username',
            title: 'Username',
            render: (_, record) => (
                <div className={styles.nameCell}>
                    <span>{`${record.first_name} ${record.last_name}`}</span>
                    {record.owner && (
                        <Chip variant='outlined' color='primary' size='xs'>
                            Owner
                        </Chip>
                    )}
                    {record.staff && (
                        <Chip variant='outlined' color='info' size='xs'>
                            SLAI staff
                        </Chip>
                    )}
                </div>
            ),
        },
        {
            key: 'email',
            title: 'Email',
            dataIndex: 'email',
        },
        {
            key: 'products',
            title: named ? 'Products' : 'Roles',
            render: (_, record) => (
                <div className={styles.productsCell}>
                    {productsWithRoles(record.products).map((p) => (
                        <Chip key={p} variant='filled' color='secondary' size='sm'>
                            {named
                                ? `${PRODUCT_LABELS[p]}: ${(record.products[p] ?? []).map(roleLabel).join(', ')}`
                                : (record.products[p] ?? []).map(roleLabel).join(', ')}
                        </Chip>
                    ))}
                </div>
            ),
        },
        {
            key: 'status',
            title: 'Status',
            render: (_, record) =>
                record.invite_status === 'pending' ? (
                    <Chip variant='filled' color='warning' size='sm'>
                        Pending
                    </Chip>
                ) : (
                    <Chip variant='filled' color='success' size='sm'>
                        Active
                    </Chip>
                ),
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (_, record) => (
                <div className={styles.actionsCell}>
                    {onMakeOwner && !record.owner && (
                        <Button
                            variant='outline-secondary'
                            size='sm'
                            icon={Crown}
                            iconOnly
                            onClick={() => onMakeOwner(record)}
                            title='Make owner'
                        />
                    )}
                    <Button
                        variant='outline-secondary'
                        size='sm'
                        icon={Pencil}
                        iconOnly
                        onClick={() => onEditUser(record)}
                        title='Edit user'
                    />
                    {(isStaff || (!record.owner && !record.staff)) && (
                        <Button
                            variant='outline-secondary'
                            size='sm'
                            icon={Trash2}
                            iconOnly
                            onClick={() => onDeleteUser(record)}
                            title='Delete user'
                        />
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
            <div className={styles.usersHeader}>
                <h2>Users {companyName ? `for ${companyName}` : ''}</h2>
                {canManage && (
                    <div className={styles.headerActions}>
                        <Button variant='outline-secondary' size='sm' onClick={onInviteUser}>
                            Invite User
                        </Button>
                        <Button variant='primary' size='sm' onClick={onAddUser}>
                            Add User
                        </Button>
                    </div>
                )}
            </div>
            <Table
                data={members}
                columns={columns}
                isLoading={isLoading}
                emptyText={emptyText}
                rowKey='id'
                hoverable
                variant='secondary'
                className={styles.usersTableWrapper}
            />
        </>
    );
};

export default UsersSection;
